'use client'

import type { ComponentProps } from 'react'

import NextLink from 'next/link'

import { canonicalHref } from '@/utils/href'

type LinkProps = ComponentProps<typeof NextLink>

/**
 * Drop-in replacement for `next/link` that normalizes string hrefs to the site's
 * canonical trailing-slash form (see {@link canonicalHref}). This keeps internal
 * navigations client-side under `trailingSlash: true` — a `<Link href="/path">`
 * without the slash otherwise degrades to a full-page reload.
 *
 * Use this everywhere instead of importing `next/link` directly. All next/link
 * props (`prefetch`, `replace`, `scroll`, `target`, `ref`, …) pass through
 * unchanged; external URLs, files, and hashes are left as-is by the normalizer.
 */
export default function Link({ href, ...props }: LinkProps) {
  return <NextLink href={normalizeHref(href)} {...props} />
}

function normalizeHref(href: LinkProps['href']): LinkProps['href'] {
  if (typeof href === 'string') return canonicalHref(href)
  if (href && typeof href === 'object' && typeof href.pathname === 'string') {
    return { ...href, pathname: canonicalHref(href.pathname) }
  }
  return href
}
